import { useMemo } from 'react';

import {
  AttachmentTypeDto,
  CarStatusDto,
  ContactStatusDto,
  CustomerStatusDto,
  EmergencyContactStatusDto,
  ProcessOfferStatusDto,
  ProcessStatusDto,
  ResourceTypeDto,
  TaskStatusDto,
  UpdateEmailContentTypeDto,
} from '@/api/generated';
import { SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

type Return = {
  booleanOptions: SelectSearchOptionModel<boolean>[];
  carStatusOptions: SelectSearchOptionModel<CarStatusDto>[];
  contactStatusOptions: SelectSearchOptionModel<ContactStatusDto>[];
  customerStatusOptions: SelectSearchOptionModel<CustomerStatusDto>[];
  emailAttachmentsTypeOptions: SelectSearchOptionModel<AttachmentTypeDto>[];
  emailContentTypeOptions: SelectSearchOptionModel<UpdateEmailContentTypeDto>[];
  emergencyContactStatusOptions: SelectSearchOptionModel<EmergencyContactStatusDto>[];
  processOfferStatusOptions: SelectSearchOptionModel<ProcessOfferStatusDto>[];
  processStatusOptions: SelectSearchOptionModel<ProcessStatusDto>[];
  resourceTypeOptions: SelectSearchOptionModel<ResourceTypeDto>[];
  taskStatusOptions: SelectSearchOptionModel<TaskStatusDto>[];
};

const EXCLUDED_EMAIL_TYPES: Set<UpdateEmailContentTypeDto> = new Set([
  UpdateEmailContentTypeDto.CakeInstallationAgencies,
  UpdateEmailContentTypeDto.CakeInstallationCompanies,
  UpdateEmailContentTypeDto.ChristmasOffer,
  UpdateEmailContentTypeDto.Easter2026,
  UpdateEmailContentTypeDto.FatThursday,
  UpdateEmailContentTypeDto.ForumEventCompanies,
  UpdateEmailContentTypeDto.OpenDayFoksal,
  UpdateEmailContentTypeDto.SixFloorTheatre,
  UpdateEmailContentTypeDto.SixFloorTheatreOpenDay,
]);

export const useGetSelectOptions = (): Return => {
  const { t } = useTranslationWithPrefix('utils.dictionaries');
  return useMemo(
    () => ({
      booleanOptions: [
        {
          label: t('booleanOptions.true'),
          value: true,
        },
        {
          label: t('booleanOptions.false'),
          value: false,
        },
      ],
      carStatusOptions: Object.values(CarStatusDto).map(status => {
        return {
          label: t(`carStatusOptions.${status}`),
          value: status,
        };
      }),
      contactStatusOptions: Object.values(ContactStatusDto).map(status => {
        return {
          label: t(`contactStatusOptions.${status}`),
          value: status,
        };
      }),
      customerStatusOptions: Object.values(CustomerStatusDto).map(status => {
        return {
          label: t(`customerStatusOptions.${status}`),
          value: status,
        };
      }),
      emailAttachmentsTypeOptions: Object.values(AttachmentTypeDto).map(it => {
        return {
          label: t(`emailAttachmentTypeOptions.${it}`),
          value: it,
        };
      }),
      emailContentTypeOptions: Object.values(UpdateEmailContentTypeDto)
        .filter(it => !EXCLUDED_EMAIL_TYPES.has(it))
        .map(it => {
          return {
            label: t(`emailContentTypeOptions.${it}`),
            value: it,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label)),
      emergencyContactStatusOptions: Object.values(EmergencyContactStatusDto).map(status => {
        return {
          label: t(`contactStatusOptions.${status}`),
          value: status,
        };
      }),
      processOfferStatusOptions: Object.values(ProcessOfferStatusDto).map(it => ({
        label: t(`processOfferStatusOptions.${it}`),
        value: it,
      })),
      processStatusOptions: Object.values(ProcessStatusDto).map(status => {
        return {
          label: t(`processStatusOptions.${status}`),
          value: status,
        };
      }),
      resourceTypeOptions: Object.values(ResourceTypeDto).map(status => {
        return {
          label: t(`resourceTypeOptions.${status}`),
          value: status,
        };
      }),
      taskStatusOptions: Object.values(TaskStatusDto).map(status => {
        return {
          label: t(`taskStatusOptions.${status}`),
          value: status,
        };
      }),
    }),
    [t],
  );
};
